import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { safeRides, shortDate } from '../lib/safe';
import { ArrowUpRight, History, User, LogOut, Plus, Award, PackageSearch } from 'lucide-react';
import { BOOK_PATH } from '../lib/routes';

const LOGO = "https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png";

const statusDot = (status) => {
  switch (status) {
    case 'completed': return 'bg-emerald-400';
    case 'pending': return 'bg-orange-400';
    case 'in-progress': return 'bg-sky-400';
    default: return 'bg-gray-500';
  }
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { currentUser, logout, getUserRides } = useAuth();
  const [recentRides, setRecentRides] = useState([]);

  useEffect(() => {
    if (currentUser) {
      setRecentRides(safeRides(getUserRides()).slice(0, 4));
    }
  }, [currentUser, getUserRides]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const initials = `${currentUser?.firstName?.charAt(0) || ''}${currentUser?.lastName?.charAt(0) || ''}`.toUpperCase();

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] lg:w-[800px] lg:h-[800px] rounded-full bg-orange-600/15 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[360px] h-[360px] lg:w-[560px] lg:h-[560px] rounded-full bg-orange-500/10 blur-[120px]" />

      <div className="relative w-full px-5 sm:px-6 lg:px-10 2xl:px-16 pb-16">
        {/* Top bar */}
        <header className="flex items-center justify-between py-6 lg:py-8">
          <button
            data-testid="logo-home"
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5 active:scale-95 transition-transform"
            aria-label="Home"
          >
            <img src={LOGO} alt="RideBuraq" className="w-10 h-10 object-contain" />
            <span className="text-2xl font-bold tracking-wide text-white">Ride<span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">Buraq</span></span>
          </button>
          <div className="flex items-center gap-2">
            <button
              data-testid="profile-link"
              onClick={() => navigate('/profile')}
              className="w-11 h-11 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-xs font-bold text-white shadow-lg shadow-orange-900/30 active:scale-95 transition-transform"
              aria-label="Profile"
            >
              {initials}
            </button>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-gray-400 hover:text-red-400 hover:border-red-400/30 active:scale-95 transition-all"
              aria-label="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* Greeting */}
        <div className="pt-6 pb-8 lg:pt-10 lg:pb-12">
          <p className="text-[11px] lg:text-sm tracking-[0.3em] uppercase text-orange-400/80 mb-2 lg:mb-3">{greeting()}</p>
          <h1 className="text-4xl lg:text-6xl font-semibold tracking-tight leading-none capitalize">
            {currentUser?.firstName} {currentUser?.lastName}
          </h1>
          <p className="text-gray-500 text-sm lg:text-lg mt-3 lg:mt-4">Where would you like to go today?</p>
        </div>

        {/* Two-column on desktop */}
        <div className="lg:grid lg:grid-cols-5 lg:gap-8 lg:items-start">
          <div className="lg:col-span-3">
            {/* Primary CTA */}
            <button
              data-testid="book-ride-cta"
              onClick={() => navigate(BOOK_PATH)}
              className="group relative w-full text-left rounded-3xl p-[1px] bg-gradient-to-br from-orange-500/70 via-orange-500/20 to-transparent active:scale-[0.99] transition-transform"
            >
              <div className="rounded-3xl bg-gradient-to-br from-gray-900 to-black px-6 py-7 lg:px-10 lg:py-12 flex items-center justify-between overflow-hidden">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-r from-orange-500/5 to-transparent" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-1">
                    <Plus className="h-4 w-4 text-orange-400" />
                    <span className="text-[11px] tracking-[0.25em] uppercase text-orange-400">New Reservation</span>
                  </div>
                  <p className="text-2xl lg:text-4xl font-semibold text-white">Book a Ride</p>
                  <p className="text-sm lg:text-base text-gray-400 mt-1">Chauffeured luxury, on your schedule</p>
                </div>
                <div className="relative w-11 h-11 lg:w-14 lg:h-14 rounded-full bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-900/40 group-hover:rotate-45 transition-transform duration-300">
                  <ArrowUpRight className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                </div>
              </div>
            </button>

            {/* Secondary actions */}
            <div className="grid grid-cols-2 gap-3 lg:gap-4 mt-3 lg:mt-4">
              <button
                data-testid="history-link"
                onClick={() => navigate('/rides')}
                className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-5 lg:py-7 flex flex-col gap-3 hover:border-orange-500/40 active:scale-[0.98] transition-all"
              >
                <History className="h-5 w-5 lg:h-6 lg:w-6 text-orange-400" />
                <span className="text-sm lg:text-base font-medium text-gray-200">Ride History</span>
              </button>
              <button
                data-testid="account-link"
                onClick={() => navigate('/profile')}
                className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-5 lg:py-7 flex flex-col gap-3 hover:border-orange-500/40 active:scale-[0.98] transition-all"
              >
                <User className="h-5 w-5 lg:h-6 lg:w-6 text-orange-400" />
                <span className="text-sm lg:text-base font-medium text-gray-200">Account</span>
              </button>
              <button
                data-testid="miles-link"
                onClick={() => navigate('/miles')}
                className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-5 lg:py-7 flex flex-col gap-3 hover:border-orange-500/40 active:scale-[0.98] transition-all"
              >
                <Award className="h-5 w-5 lg:h-6 lg:w-6 text-orange-400" />
                <span className="text-sm lg:text-base font-medium text-gray-200">Buraq Miles</span>
              </button>
              <button
                data-testid="lost-found-link"
                onClick={() => navigate('/lost-found')}
                className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-5 lg:py-7 flex flex-col gap-3 hover:border-orange-500/40 active:scale-[0.98] transition-all"
              >
                <PackageSearch className="h-5 w-5 lg:h-6 lg:w-6 text-orange-400" />
                <span className="text-sm lg:text-base font-medium text-gray-200">Lost &amp; Found</span>
              </button>
            </div>
          </div>

          {/* Recent */}
          <div className="mt-10 lg:mt-0 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[11px] tracking-[0.3em] uppercase text-gray-500">Recent</p>
              {recentRides.length > 0 && (
                <button
                  data-testid="view-all-rides"
                  onClick={() => navigate('/rides')}
                  className="text-xs text-orange-400 hover:text-orange-300 transition-colors"
                >
                  View all
                </button>
              )}
            </div>

            {recentRides.length > 0 ? (
              <div className="divide-y divide-white/5 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
                {recentRides.map((ride) => (
                  <button
                    key={ride.id}
                    onClick={() => navigate('/rides')}
                    className="w-full text-left px-4 py-4 flex items-center justify-between hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="min-w-0 pr-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${statusDot(ride.status)}`} />
                        <p className="text-sm font-medium text-white truncate">{ride.pickupAddress}</p>
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-1 pl-3.5">to {ride.dropoffAddress}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-white">${Number(ride.estimatedFare || 0).toFixed(2)}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {shortDate(ride.createdAt, { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-white/10 px-6 py-10 lg:py-16 text-center">
                <p className="text-gray-500 text-sm mb-4">No rides yet — your journeys will appear here.</p>
                <button
                  data-testid="first-ride-btn"
                  onClick={() => navigate(BOOK_PATH)}
                  className="text-sm font-medium text-orange-400 hover:text-orange-300 transition-colors"
                >
                  Book your first ride →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
